import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { lazy, Suspense } from "react";

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
    <div className="spinner"></div>
  </div>
);

// Critical path pages - loaded immediately
import LandingPage from "@/pages/LandingPage";
import Questionnaire from "@/pages/Questionnaire";
import ThankYou from "@/pages/ThankYou";

// Admin pages - lazy loaded (only loaded when user navigates to admin)
const AdminLogin = lazy(() => import("@/pages/admin/Login"));
const ForgotPassword = lazy(() => import("@/pages/admin/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/admin/ResetPassword"));
const AdminDashboard = lazy(() => import("@/pages/admin/Dashboard"));
const Participants = lazy(() => import("@/pages/admin/Participants"));
const ParticipantDetail = lazy(() => import("@/pages/admin/ParticipantDetail"));
const CoachProfile = lazy(() => import("@/pages/admin/CoachProfile"));
const KnowledgeBasePage = lazy(() => import("@/pages/admin/KnowledgeBase"));
const AdminManagement = lazy(() => import("@/pages/admin/AdminManagement"));
const PlansManagement = lazy(() => import("@/pages/admin/PlansManagement"));
const CouponsManagement = lazy(() => import("@/pages/admin/CouponsManagement"));
const OrdersManagement = lazy(() => import("@/pages/admin/OrdersManagement"));
const MasterSettings = lazy(() => import("@/pages/admin/MasterSettings"));
const SecuritySettings = lazy(() => import("@/pages/admin/SecuritySettings"));
// RBAC Management pages
const UserManagement = lazy(() => import("@/pages/admin/UserManagement"));
const RoleManagement = lazy(() => import("@/pages/admin/RoleManagement"));
const AuditLogs = lazy(() => import("@/pages/admin/AuditLogs"));
// Nutraceutical Products
const ProductsManagement = lazy(() => import("@/pages/admin/ProductsManagement"));

// User Portal pages - lazy loaded
const UserLogin = lazy(() => import("@/pages/user/Login"));
const UserDashboard = lazy(() => import("@/pages/user/Dashboard"));
const UserProfile = lazy(() => import("@/pages/user/Profile"));

// Auth contexts
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { UserAuthProvider, useUserAuth } from "@/context/UserAuthContext";

// Protected Route Component for Admin
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

// Protected Route Component for User Portal
const UserProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useUserAuth();
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/user/login" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {/* Public Routes - No lazy loading for critical user path */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/questionnaire" element={<Questionnaire />} />
        <Route path="/thank-you" element={<ThankYou />} />
        
        {/* Admin Auth Routes - Lazy loaded */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/forgot-password" element={<ForgotPassword />} />
        <Route path="/admin/reset-password" element={<ResetPassword />} />
        
        {/* Protected Admin Routes - Lazy loaded */}
        <Route path="/admin" element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/admin/participants" element={
          <ProtectedRoute>
            <Participants />
          </ProtectedRoute>
        } />
        <Route path="/admin/participants/:id" element={
          <ProtectedRoute>
            <ParticipantDetail />
          </ProtectedRoute>
        } />
        <Route path="/admin/coach" element={
          <ProtectedRoute>
            <CoachProfile />
          </ProtectedRoute>
        } />
        <Route path="/admin/knowledge-base" element={
          <ProtectedRoute>
            <KnowledgeBasePage />
          </ProtectedRoute>
        } />
        <Route path="/admin/users" element={
          <ProtectedRoute>
            <AdminManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/plans" element={
          <ProtectedRoute>
            <PlansManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/coupons" element={
          <ProtectedRoute>
            <CouponsManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/orders" element={
          <ProtectedRoute>
            <OrdersManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/settings" element={
          <ProtectedRoute>
            <MasterSettings />
          </ProtectedRoute>
        } />
        <Route path="/admin/security" element={
          <ProtectedRoute>
            <SecuritySettings />
          </ProtectedRoute>
        } />
        {/* RBAC Management Routes */}
        <Route path="/admin/user-management" element={
          <ProtectedRoute>
            <UserManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/roles" element={
          <ProtectedRoute>
            <RoleManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/audit-logs" element={
          <ProtectedRoute>
            <AuditLogs />
          </ProtectedRoute>
        } />
        {/* Nutraceutical Products */}
        <Route path="/admin/products" element={
          <ProtectedRoute>
            <ProductsManagement />
          </ProtectedRoute>
        } />
        
        {/* ===================== USER PORTAL ROUTES ===================== */}
        <Route path="/user/login" element={<UserLogin />} />
        <Route path="/user/dashboard" element={
          <UserProtectedRoute>
            <UserDashboard />
          </UserProtectedRoute>
        } />
        <Route path="/user/profile" element={
          <UserProtectedRoute>
            <UserProfile />
          </UserProtectedRoute>
        } />
        {/* Redirect /user to /user/dashboard */}
        <Route path="/user" element={<Navigate to="/user/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <UserAuthProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </UserAuthProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
